// Small shared render-states: a centered spinner, a typed error panel, and an
// empty-state note — same pattern as admin's `components/states.tsx` (errors
// surfaced, never swallowed — doctrine 1.5). Kept app-local (not promoted):
// it's ~15 lines of CSS and two icons, and every consumer already has
// `Spinner`/`Skeleton`/`Button` from `@bi/shared`.
import { Button, Spinner } from "@bi/shared";

import { ApiError } from "../lib/api";
import { AlertIcon, InboxIcon } from "./icons";

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="bi-state" role="status" aria-live="polite">
      <Spinner />
      <span className="bi-state__label">{label}</span>
    </div>
  );
}

export function EmptyState({ title, message }: { title: string; message?: string }) {
  return (
    <div className="bi-state">
      <span className="bi-state__icon" aria-hidden="true">
        <InboxIcon />
      </span>
      <p className="bi-state__title">{title}</p>
      {message ? <p className="bi-state__message">{message}</p> : null}
    </div>
  );
}

/** Turn an unknown query error into a readable line. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

export function ErrorState({
  error,
  title = "Couldn't load that",
  onRetry,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="bi-state" role="alert">
      <span className="bi-state__icon bi-state__icon--danger" aria-hidden="true">
        <AlertIcon />
      </span>
      <p className="bi-state__title">{title}</p>
      <p className="bi-state__message">{errorMessage(error)}</p>
      {onRetry ? (
        <Button variant="outline" tone="neutral" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}
